import { IRowHookOutputInternal } from 'dromo-uploader-js'
import {
  ExistingRecordMatch,
  ProposedSiteAction,
  SiteActionAPIResponse,
  SiteListType,
} from '@black-bear-energy/black-bear-energy-common'
import { SiteAction } from 'common/types'
import {
  uploadMatchedRecordSiteActionOptions,
  uploadUnmatchedRecordSiteActionOptions,
  existingRecordSiteActionOptions,
} from '../DromoUploader/commonFieldSchemas'

export function setSiteAction({
  siteListType,
  siteAction,
  record,
  isUpdate,
  getMatchingDBRecordDecription,
}: {
  siteListType: SiteListType
  siteAction: ProposedSiteAction<{ [key: string]: string | number }>
  record: IRowHookOutputInternal
  isUpdate: boolean
  getMatchingDBRecordDecription: (existingRecord: ExistingRecordMatch) => string
}): IRowHookOutputInternal {
  const recordCopy = { ...record }
  const rowSiteActionCopy = { ...recordCopy.row.siteAction }
  // remove any warnings we may have previously added
  rowSiteActionCopy.info = []

  switch (siteAction.action) {
    case SiteActionAPIResponse.Update:
      const message = `Match found:<br><br>${getMatchingDBRecordDecription(
        siteAction.existingRecord
      )}`
      rowSiteActionCopy.info = [{ message, level: 'info' }]
      rowSiteActionCopy.selectOptions = uploadMatchedRecordSiteActionOptions
      recordCopy.row.existingRecordId.value = siteAction.existingRecord.id
      rowSiteActionCopy.value = SiteAction.Update
      break
    case SiteActionAPIResponse.Review:
      const reviewMessage = getSiteReviewMessage(
        siteAction,
        record,
        isUpdate,
        getMatchingDBRecordDecription
      )
      if (reviewMessage) {
        rowSiteActionCopy.info = [{ message: reviewMessage, level: 'info' }]
      }
      if (siteAction.potentialMatch) {
        rowSiteActionCopy.selectOptions = uploadMatchedRecordSiteActionOptions
        recordCopy.row.existingRecordId.value = siteAction.potentialMatch.id
      } else {
        rowSiteActionCopy.selectOptions = uploadUnmatchedRecordSiteActionOptions
      }
      rowSiteActionCopy.value = SiteAction.Review
      break
    case SiteActionAPIResponse.Sold:
      if (siteListType !== SiteListType.Sold) {
        throw new Error(
          'User must review sold sites if import type is not sold sites only'
        )
      }
      rowSiteActionCopy.selectOptions = existingRecordSiteActionOptions
      recordCopy.row.existingRecordId.value = siteAction.record.id
      rowSiteActionCopy.value = SiteAction.Sold
      break
    case SiteActionAPIResponse.New:
      rowSiteActionCopy.selectOptions = uploadUnmatchedRecordSiteActionOptions
      rowSiteActionCopy.value = SiteAction.New
      break
    default:
      throw new Error(`Unrecognized site action: ${JSON.stringify(siteAction)}`)
  }
  recordCopy.row.siteAction = rowSiteActionCopy
  return recordCopy
}

function getSiteReviewMessage(
  siteAction: ProposedSiteAction,
  record: IRowHookOutputInternal,
  isUpdate: boolean,
  getMatchingDBRecordDecription: (existingRecord: ExistingRecordMatch) => string
): string | null {
  if (siteAction.action !== SiteActionAPIResponse.Review) {
    return null
  }

  if (siteAction.isUnmatchedSoldSite) {
    return 'No existing record found that matches this site'
  }

  if (siteAction.potentialMatch) {
    const potentialMatchDescription = getMatchingDBRecordDecription(
      siteAction.potentialMatch
    )
    if (!record.row.isGeocodeValid.value && !isUpdate) {
      return `Address could not be geocoded, but location fields were copied from this potential match:<br><br>${potentialMatchDescription}`
    } else {
      return `Potential match found:<br><br>${potentialMatchDescription}`
    }
  }

  if (siteAction.isMissingInfo) {
    return 'Required info missing'
  }

  return null
}
